import axios from 'axios';

import { APIResponseParsed } from 'types/response';
import { patchRequest, postRequest } from 'utils/HttpRequest/http-request-utils';

import { parseAxiosError } from './utils/parse-axios-error';

// ==============================
// TYPES
// ==============================
type CreatedBarnOwlAccount = {
  externalAccountId: string;
  email: string;
  timezone: string;
};

export type CreateBarnOwlAccountDto = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  timezone: string;
  password: string;
};

type ConvertedBarnOwlAccount = {
  accountId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  timezone: string;
};

export type ConvertBarnOwlAccountDto = {
  accountId: string;
  siteId: number;
};

export type AttachBarnOwlCameraDto = {
  cameraName: string;
  IMEI: string;
  ICCID: string;
  siteId: number;
};

type BarnOwlCamera = {
  cameraId: string;
  IMEI: string;
  cameraName: string;
  cameraModel: string;
  firmware: string;
  cameraStatus: 'Active' | 'Snoozed' | 'Suspended';
  accountId: string;
  parentId: string;
};

// ==============================
// REQUESTS
// ==============================
async function createBarnOwlAccount(
  enterpriseId: number,
  dto: CreateBarnOwlAccountDto
): Promise<APIResponseParsed<CreatedBarnOwlAccount>> {
  try {
    const response = await postRequest<
      CreatedBarnOwlAccount,
      CreateBarnOwlAccountDto
    >(`/integrations/barn-owl/enterprise/${enterpriseId}/account`, dto);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function attachBarnOwlCamera(
  enterpriseId: number,
  dto: AttachBarnOwlCameraDto
): Promise<APIResponseParsed<BarnOwlCamera>> {
  try {
    const response = await postRequest<BarnOwlCamera, AttachBarnOwlCameraDto>(
      `/integrations/barn-owl/enterprise/${enterpriseId}/account/attach-camera`,
      dto
    );
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function convertBarnOwlAccount(
  enterpriseId: number,
  dto: ConvertBarnOwlAccountDto
): Promise<APIResponseParsed<ConvertedBarnOwlAccount>> {
  try {
    const response = await patchRequest<
      ConvertedBarnOwlAccount,
      ConvertBarnOwlAccountDto
    >(`/integrations/barn-owl/enterprise/${enterpriseId}/account/convert`, dto);
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

export default {
  createBarnOwlAccount,
  convertBarnOwlAccount,
  attachBarnOwlCamera
};
